import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Icon, Spin } from 'antd';
import {
  extractYoutubeId,
  formatPauseClock,
  buildYoutubeEmbedSrc,
  injectYoutubeTiming,
  normalizePauseMarks,
  nextPauseAfter,
} from '../../utils/youtube';
import { loadYoutubeApi, postIframeCommand } from './youtubeApi';
import StageOverlay from './StageOverlay';

export { formatPauseClock, extractYoutubeId } from '../../utils/youtube';

/**
 * YouTube iframe + wall-clock section bookmarks (incident log).
 * - Play advances until the next bookmark, then pauses (Resume continues).
 * - seekNonce jumps to a bookmark and pauses there.
 * - Questions belong to the video (walkthrough), not individual bookmarks.
 */
function ScenarioVideoPlayer({
  video,
  overlay,
  onTimeChange,
  pauseMarks = [],
  seekSeconds,
  seekNonce = 0,
  onReachedPause,
  pauseLabel,
}) {
  const reactId = useId();
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const onTimeChangeRef = useRef(onTimeChange);
  const onReachedPauseRef = useRef(onReachedPause);
  const pauseMarksRef = useRef([]);
  const targetPauseRef = useRef(null);
  const resumeFromRef = useRef(0);
  const pausedAtRef = useRef(null);
  const segmentAnchorRef = useRef(null);
  const pollIdRef = useRef(null);
  const pauseEnforceIdRef = useRef(null);
  const playingSegmentRef = useRef(false);

  const [isPausedAtMark, setIsPausedAtMark] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [isPlayingSegment, setIsPlayingSegment] = useState(false);
  const [segmentSeconds, setSegmentSeconds] = useState(null);
  const [activePauseSeconds, setActivePauseSeconds] = useState(null);
  const [mediaError, setMediaError] = useState(null);

  const startSeconds = Number(video?.startSeconds) || 0;
  const marks = useMemo(
    () => normalizePauseMarks(pauseMarks, video?.pauseAtSeconds),
    [pauseMarks, video?.pauseAtSeconds],
  );
  const videoUrl = String(video?.videoUrl || '').trim();
  const videoHtml = String(video?.videoHtml || '').trim();
  const youtubeId =
    video?.youtubeVideoId || extractYoutubeId(videoHtml) || extractYoutubeId(videoUrl);
  const useYoutubePlayer = Boolean(youtubeId);
  const useHtml5 = Boolean(videoUrl && !youtubeId);
  const iframeId = `te-yt-${video?.videoId || 'x'}-${String(reactId).replace(/:/g, '')}`;
  const embedSrc = useYoutubePlayer ? buildYoutubeEmbedSrc(youtubeId, { startSeconds }) : '';

  useEffect(() => {
    onTimeChangeRef.current = onTimeChange;
  }, [onTimeChange]);

  useEffect(() => {
    onReachedPauseRef.current = onReachedPause;
  }, [onReachedPause]);

  useEffect(() => {
    pauseMarksRef.current = marks;
  }, [marks]);

  const stopPoll = useCallback(() => {
    if (pollIdRef.current) {
      window.clearInterval(pollIdRef.current);
      pollIdRef.current = null;
    }
  }, []);

  const stopPauseEnforce = useCallback(() => {
    if (pauseEnforceIdRef.current) {
      window.clearInterval(pauseEnforceIdRef.current);
      pauseEnforceIdRef.current = null;
    }
  }, []);

  const forcePauseYoutube = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      try {
        if (typeof player.pauseVideo === 'function') player.pauseVideo();
        if (typeof player.mute === 'function') player.mute();
      } catch {
        // ignore
      }
    }
    postIframeCommand(iframeRef.current, 'pauseVideo');
    postIframeCommand(iframeRef.current, 'mute');
  }, []);

  const forcePlayYoutube = useCallback(
    (fromSeconds) => {
      const from = Number.isFinite(fromSeconds) ? fromSeconds : startSeconds;
      const player = playerRef.current;
      if (player) {
        try {
          if (typeof player.unMute === 'function') player.unMute();
          if (typeof player.seekTo === 'function') player.seekTo(from, true);
          if (typeof player.playVideo === 'function') player.playVideo();
          return;
        } catch {
          // fall through
        }
      }
      postIframeCommand(iframeRef.current, 'unMute');
      postIframeCommand(iframeRef.current, 'seekTo', [from, true]);
      postIframeCommand(iframeRef.current, 'playVideo');
    },
    [startSeconds],
  );

  const markPausedAt = useCallback(
    (seconds) => {
      const at = Number(seconds);
      if (!Number.isFinite(at)) return;

      pausedAtRef.current = at;
      targetPauseRef.current = at;
      segmentAnchorRef.current = null;
      playingSegmentRef.current = false;
      setHasStarted(true);
      setIsPausedAtMark(true);
      setIsPlayingSegment(false);
      setActivePauseSeconds(at);
      setSegmentSeconds(at);
      stopPoll();

      forcePauseYoutube();
      stopPauseEnforce();
      let attempts = 0;
      pauseEnforceIdRef.current = window.setInterval(() => {
        attempts += 1;
        forcePauseYoutube();
        const player = playerRef.current;
        const state = player && typeof player.getPlayerState === 'function' ? player.getPlayerState() : null;
        if (state === 2 || state === 0 || state === 5 || attempts >= 24) stopPauseEnforce();
      }, 250);

      const el = videoRef.current;
      if (el && !el.paused) el.pause();

      if (typeof onTimeChangeRef.current === 'function') onTimeChangeRef.current(at);
      if (typeof onReachedPauseRef.current === 'function') onReachedPauseRef.current(at);
    },
    [forcePauseYoutube, stopPauseEnforce, stopPoll],
  );

  const startSegmentPoll = useCallback(
    (fromSeconds) => {
      stopPoll();
      const from = Number.isFinite(fromSeconds) ? fromSeconds : startSeconds;
      const target = nextPauseAfter(pauseMarksRef.current, from);
      resumeFromRef.current = from;
      targetPauseRef.current = Number.isFinite(target) ? target : null;
      segmentAnchorRef.current = { wallMs: Date.now(), videoSeconds: from };
      playingSegmentRef.current = true;
      setIsPlayingSegment(true);
      setSegmentSeconds(from);

      pollIdRef.current = window.setInterval(() => {
        const el = videoRef.current;
        // Prefer real media time for HTML5 so native pause/seek stays aligned.
        let current = null;
        if (el && Number.isFinite(el.currentTime)) {
          current = el.currentTime;
          // Keep wall anchor in sync for YouTube fallback path.
          segmentAnchorRef.current = { wallMs: Date.now(), videoSeconds: current };
        } else {
          const anchor = segmentAnchorRef.current;
          if (!anchor) return;
          current = anchor.videoSeconds + (Date.now() - anchor.wallMs) / 1000;
        }

        setSegmentSeconds(current);
        if (typeof onTimeChangeRef.current === 'function') onTimeChangeRef.current(current);

        const pauseTarget = targetPauseRef.current;
        if (Number.isFinite(pauseTarget) && current >= pauseTarget - 0.15) {
          markPausedAt(pauseTarget);
        }
      }, 200);
    },
    [markPausedAt, startSeconds, stopPoll],
  );

  const syncHtml5Playing = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    const from = Number.isFinite(el.currentTime) ? el.currentTime : startSeconds;
    setMediaError(null);
    setHasStarted(true);
    setIsPausedAtMark(false);
    setActivePauseSeconds(null);
    pausedAtRef.current = null;
    stopPauseEnforce();
    startSegmentPoll(from);
  }, [startSeconds, startSegmentPoll, stopPauseEnforce]);

  const syncHtml5Paused = useCallback(() => {
    const el = videoRef.current;
    const at = el && Number.isFinite(el.currentTime) ? el.currentTime : segmentSeconds;
    stopPoll();
    playingSegmentRef.current = false;
    setIsPlayingSegment(false);
    if (Number.isFinite(at)) {
      setSegmentSeconds(at);
      if (typeof onTimeChangeRef.current === 'function') onTimeChangeRef.current(at);
      // If native pause lands on/near a bookmark, show the same pause shell.
      const marks = pauseMarksRef.current || [];
      const hit = marks.find((mark) => Math.abs(mark - at) <= 0.4);
      if (hit != null) {
        markPausedAt(hit);
        return;
      }
      pausedAtRef.current = at;
    }
  }, [markPausedAt, segmentSeconds, stopPoll]);

  useEffect(() => {
    setIsPausedAtMark(false);
    setHasStarted(false);
    setApiReady(false);
    setIsMediaLoading(true);
    setIsPlayingSegment(false);
    setSegmentSeconds(null);
    setActivePauseSeconds(null);
    setMediaError(null);
    playerRef.current = null;
    pausedAtRef.current = null;
    targetPauseRef.current = null;
    segmentAnchorRef.current = null;
    playingSegmentRef.current = false;
    resumeFromRef.current = startSeconds;
    stopPoll();
    stopPauseEnforce();
    if (typeof onTimeChangeRef.current === 'function') onTimeChangeRef.current(startSeconds);
  }, [video?.videoId, videoUrl, youtubeId, startSeconds, stopPoll, stopPauseEnforce]);

  useEffect(() => {
    if (useYoutubePlayer && apiReady) setIsMediaLoading(false);
  }, [useYoutubePlayer, apiReady]);

  useEffect(() => {
    if (!useHtml5) return;
    const el = videoRef.current;
    if (el && el.readyState >= 2) setIsMediaLoading(false);
  }, [useHtml5, video?.videoId, videoUrl]);

  useEffect(() => {
    if (!useYoutubePlayer) return undefined;

    let cancelled = false;

    loadYoutubeApi()
      .then((YT) => {
        if (cancelled) return;
        if (!document.getElementById(iframeId)) return;

        try {
          const player = new YT.Player(iframeId, {
            events: {
              onReady: (event) => {
                if (cancelled) return;
                playerRef.current = event.target;
                setApiReady(true);
                setIsMediaLoading(false);
                if (playingSegmentRef.current) forcePlayYoutube(resumeFromRef.current);
              },
              onStateChange: (event) => {
                if (cancelled) return;
                if (event.data === YT.PlayerState.PLAYING) {
                  setHasStarted(true);
                  setIsMediaLoading(false);
                  if (playingSegmentRef.current) {
                    setIsPausedAtMark(false);
                    return;
                  }
                  if (pausedAtRef.current != null) forcePauseYoutube();
                }
              },
            },
          });
          playerRef.current = player;
        } catch {
          if (!cancelled) {
            setApiReady(false);
            setIsMediaLoading(false);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setIsMediaLoading(false);
      });

    return () => {
      cancelled = true;
      playerRef.current = null;
    };
  }, [useYoutubePlayer, iframeId, video?.videoId, forcePlayYoutube, forcePauseYoutube]);

  const beginPlaybackFrom = useCallback(
    (fromSeconds) => {
      const from = Number.isFinite(fromSeconds) ? fromSeconds : startSeconds;
      pausedAtRef.current = null;
      stopPauseEnforce();
      setHasStarted(true);
      setIsPausedAtMark(false);
      setActivePauseSeconds(null);
      playingSegmentRef.current = true;

      forcePlayYoutube(from);

      const el = videoRef.current;
      if (el) {
        el.currentTime = from;
        el.play().catch(() => {});
      }

      startSegmentPoll(from);
    },
    [forcePlayYoutube, startSeconds, startSegmentPoll, stopPauseEnforce],
  );

  // Jump to an incident-log time and pause there.
  useEffect(() => {
    if (!seekNonce) return undefined;
    if (!Number.isFinite(Number(seekSeconds))) return undefined;
    const at = Number(seekSeconds);
    setHasStarted(true);
    playingSegmentRef.current = false;
    setIsPlayingSegment(false);

    const player = playerRef.current;
    if (player && typeof player.seekTo === 'function') {
      try {
        player.seekTo(at, true);
      } catch {
        // ignore
      }
    } else if (iframeRef.current) {
      postIframeCommand(iframeRef.current, 'seekTo', [at, true]);
    }

    const el = videoRef.current;
    if (el) {
      try {
        el.currentTime = at;
        el.pause();
      } catch {
        // ignore
      }
    }

    markPausedAt(at);
    return undefined;
  }, [seekNonce, seekSeconds, markPausedAt]);

  const playYoutube = useCallback(() => {
    const from = pausedAtRef.current != null ? pausedAtRef.current : startSeconds;
    beginPlaybackFrom(from);
  }, [beginPlaybackFrom, startSeconds]);

  const resumeFromPause = useCallback(() => {
    const from = pausedAtRef.current != null ? pausedAtRef.current : startSeconds;
    beginPlaybackFrom(from);
  }, [beginPlaybackFrom, startSeconds]);

  const playHtml5 = useCallback(() => {
    const el = videoRef.current;
    const fromMedia = el && Number.isFinite(el.currentTime) ? el.currentTime : null;
    const from =
      pausedAtRef.current != null
        ? pausedAtRef.current
        : fromMedia != null && fromMedia > startSeconds
          ? fromMedia
          : startSeconds;
    beginPlaybackFrom(from);
  }, [beginPlaybackFrom, startSeconds]);

  useEffect(
    () => () => {
      stopPoll();
      stopPauseEnforce();
    },
    [stopPoll, stopPauseEnforce],
  );

  const nextMark = Number.isFinite(segmentSeconds) ? nextPauseAfter(marks, segmentSeconds) : marks[0];
  const secondsToPause =
    isPlayingSegment && Number.isFinite(nextMark) && Number.isFinite(segmentSeconds)
      ? Math.max(0, Math.ceil(nextMark - segmentSeconds))
      : null;

  const showLoadingStatus = isMediaLoading && !mediaError && !isPausedAtMark;
  const loadingStatus = (
    <div className="testing-exercising-media__loading" role="status" aria-live="polite">
      <div className="testing-exercising-media__loading-panel">
        <Spin size="large" />
        <p className="testing-exercising-media__loading-title">Loading video…</p>
        <p className="testing-exercising-media__loading-text">
          This can take a moment on slower connections.
        </p>
      </div>
    </div>
  );

  if (useYoutubePlayer) {
    return (
      <div className="testing-exercising-media__video-player">
        <iframe
          ref={iframeRef}
          id={iframeId}
          title={video?.heading || 'Scenario video'}
          className="testing-exercising-media__yt-iframe"
          src={embedSrc}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />

        {showLoadingStatus ? loadingStatus : null}

        {!hasStarted && !isPausedAtMark && !showLoadingStatus ? (
          <div className="testing-exercising-media__play-gate">
            {video?.thumbnail ? (
              <img src={video.thumbnail} alt="" className="testing-exercising-media__play-gate-poster" />
            ) : null}
            <Button type="primary" size="large" className="testing-exercising-media__play-btn" onClick={playYoutube}>
              <Icon type="caret-right" />
              Play video
            </Button>
            <p className="testing-exercising-media__play-hint">
              Sample clip (~2 min). It pauses at each section bookmark. Click an incident-log row to jump there.
            </p>
          </div>
        ) : null}

        {hasStarted && !isPausedAtMark && secondsToPause != null ? (
          <div className="testing-exercising-media__pause-countdown" role="status">
            Next pause in ~{secondsToPause}s
            {pauseLabel ? ` (${pauseLabel})` : ''}
          </div>
        ) : null}

        {isPausedAtMark ? (
          <div className="testing-exercising-media__pause-shield">
            <StageOverlay overlay={overlay} />
            <div className="testing-exercising-media__pause-banner" role="status">
              <strong>Paused{pauseLabel ? ` — ${pauseLabel}` : ''}</strong>
              <span>
                {Number.isFinite(activePauseSeconds)
                  ? `At ${formatPauseClock(activePauseSeconds)} on the sample clip. `
                  : ''}
                Answer this video&apos;s questions anytime. Resume to continue, or Continue below for the next video.
              </span>
              <Button type="primary" className="testing-exercising-media__resume-btn" onClick={resumeFromPause}>
                Resume video
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (useHtml5) {
    return (
      <div className="testing-exercising-media__video-player">
        <video
          key={`${video.videoId}-${videoUrl}`}
          ref={videoRef}
          controls={hasStarted && !isPausedAtMark && !mediaError}
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          playsInline
          preload="auto"
          poster={video.thumbnail || undefined}
          src={videoUrl}
          onContextMenu={(event) => event.preventDefault()}
          onLoadStart={() => {
            setIsMediaLoading(true);
            setMediaError(null);
          }}
          onWaiting={() => setIsMediaLoading(true)}
          onLoadedData={() => setIsMediaLoading(false)}
          onCanPlay={() => setIsMediaLoading(false)}
          onPlaying={() => setIsMediaLoading(false)}
          onPlay={syncHtml5Playing}
          onPause={syncHtml5Paused}
          onSeeked={() => {
            const el = videoRef.current;
            if (!el || !Number.isFinite(el.currentTime)) return;
            const at = el.currentTime;
            setSegmentSeconds(at);
            if (typeof onTimeChangeRef.current === 'function') onTimeChangeRef.current(at);
            if (!el.paused && playingSegmentRef.current) startSegmentPoll(at);
            else if (!el.paused) syncHtml5Playing();
          }}
          onError={() => {
            setIsMediaLoading(false);
            setMediaError(
              'Video failed to load. Check the blob URL (path/filename) or add a read SAS if the container is private.',
            );
          }}
        >
          <track kind="captions" />
        </video>
        {mediaError ? (
          <div className="testing-exercising-media__play-gate" role="alert">
            <p className="testing-exercising-media__media-error">{mediaError}</p>
          </div>
        ) : null}
        {showLoadingStatus ? loadingStatus : null}
        {!hasStarted && !isPausedAtMark && !mediaError && !showLoadingStatus ? (
          <div className="testing-exercising-media__play-gate">
            <Button type="primary" size="large" className="testing-exercising-media__play-btn" onClick={playHtml5}>
              <Icon type="caret-right" />
              Play video
            </Button>
          </div>
        ) : null}
        {isPausedAtMark ? (
          <div className="testing-exercising-media__pause-shield">
            <StageOverlay overlay={overlay} />
            <div className="testing-exercising-media__pause-banner" role="status">
              <strong>Paused{pauseLabel ? ` — ${pauseLabel}` : ''}</strong>
              <Button type="primary" className="testing-exercising-media__resume-btn" onClick={resumeFromPause}>
                Resume video
              </Button>
            </div>
          </div>
        ) : null}
        {hasStarted && !isPausedAtMark && !mediaError && !showLoadingStatus && secondsToPause != null ? (
          <div className="testing-exercising-media__pause-countdown" role="status">
            Next pause in ~{secondsToPause}s
            {pauseLabel ? ` (${pauseLabel})` : ''}
          </div>
        ) : null}
      </div>
    );
  }

  if (videoHtml) {
    return (
      <div
        className="testing-exercising-media__embed"
        dangerouslySetInnerHTML={{
          __html: injectYoutubeTiming(videoHtml, { startSeconds }),
        }}
      />
    );
  }

  return (
    <div className="testing-exercising-media__placeholder" role="status" aria-live="polite">
      {video?.thumbnail ? (
        <img src={video.thumbnail} alt="" className="testing-exercising-media__placeholder-poster" />
      ) : null}
      <div className="testing-exercising-media__placeholder-body">
        <Icon type="play-square" className="testing-exercising-media__placeholder-icon" aria-hidden />
        <p className="testing-exercising-media__placeholder-title">No video available</p>
        <p className="testing-exercising-media__placeholder-text">
          A video URL was not provided for this section. You can still review the incident log and answer
          the questions below.
        </p>
      </div>
      <StageOverlay overlay={overlay} />
    </div>
  );
}

export default ScenarioVideoPlayer;
